// Время для полей «когда»: отложенная отправка (ScheduleSendMenu,
// ScheduledQueue) и напоминания (ReminderEditor, reminders.ts). Всё — в
// часовом поясе браузера: рекрутёр выбирает «завтра в 11:00» по своим часам,
// а на сервер уходит уже ISO со смещением.
//
// `now` — параметром, а не `new Date()` внутри: пресеты и проверки срока
// тестируются на заданной дате.

import type { ScheduledChatMessage } from "@/api/client";

/** <input type="datetime-local"> хочет локальное `YYYY-MM-DDTHH:MM`. */
export function toLocalInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Значение datetime-local обратно в дату; пустое или битое — null. */
export function fromLocalInput(value: string): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Через `m` минут от `now`. */
export function inMinutes(m: number, now: Date = new Date()): Date {
  const d = new Date(now);
  d.setMinutes(d.getMinutes() + m);
  return d;
}

/** Через `days` суток от `now`, ровно в `hour`:`minute` по местному времени. */
export function dayAt(days: number, hour: number, minute = 0, now: Date = new Date()): Date {
  const d = new Date(now);
  d.setDate(d.getDate() + days);
  d.setHours(hour, minute, 0, 0);
  return d;
}

// ── Окно «Запланировать отправку сообщения» (PRI-260) ─────────────────────

/** Вкладки окна: по времени, за час или за день до встречи кандидата. */
export type SendTab = "time" | "hour" | "day";

export const SEND_TABS: readonly { key: SendTab; label: string }[] = [
  { key: "time", label: "По времени" },
  { key: "hour", label: "За час" },
  { key: "day", label: "За день" },
];

/** Подсказка под вкладкой «За час» / «За день» — как в эталоне. */
export const RELATIVE_NOTE: Record<"hour" | "day", string> = {
  hour: "Сообщение уйдёт за час до встречи кандидата.",
  day: "Сообщение уйдёт за день до встречи кандидата.",
};

/** За сколько минут до встречи уходит сообщение вкладки (chat-service
 *  `BEFORE_MEETING_MIN`). */
export const BEFORE_MEETING_MIN = { hour: 60, day: 1440 } as const;

/** Подпись привязанной к встрече отправки в очереди: «за час до встречи»;
 *  null — отправка в заданное время (или незнакомый срок). */
export function beforeMeetingLabel(min: number | null | undefined): string | null {
  if (min === BEFORE_MEETING_MIN.hour) return "за час до встречи";
  if (min === BEFORE_MEETING_MIN.day) return "за день до встречи";
  return null;
}

/** Когда отправить: в заданное время или за N минут до встречи кандидата (время
 *  тогда считает и держит за встречей сервер). */
export type SendWhen =
  | { kind: "at"; at: Date }
  | { kind: "before"; minutes: (typeof BEFORE_MEETING_MIN)[keyof typeof BEFORE_MEETING_MIN] };

/** Выбор вкладки в `SendWhen`; null — по времени, а время не задано / битое. */
export function sendWhenOf(tab: SendTab, at: Date | null): SendWhen | null {
  if (tab === "time") return at ? { kind: "at", at } : null;
  return { kind: "before", minutes: BEFORE_MEETING_MIN[tab] };
}

/** Что не так с выбранным временем; null — можно отправлять. Бэкенд требует
 *  запас в 5 секунд (`_MIN_LEAD_S`) — просим чуть больше, чтобы «через минуту»
 *  не отвалилось по дороге. */
export function atError(at: Date | null, now: Date = new Date()): string | null {
  if (!at || Number.isNaN(at.getTime())) return "Некорректная дата";
  if (at.getTime() < now.getTime() + 10_000) return "Время должно быть в будущем";
  return null;
}

// ── Ошибки действий в полоске отложенных ──────────────────────────────────

/** Что рекрутёр делал со строкой полоски: отправлял сейчас, отменял, правил. */
export type QueueAction = "send" | "cancel" | "edit";

const hasOwn = (o: object, k: string) => Object.prototype.hasOwnProperty.call(o, k);

/** Строку уже забрал исполнитель или она ушла/закрыта (`scheduled_not_pending`):
 *  у каждого действия свои слова — «отменить поздно» не то же, что «править». */
const NOT_PENDING: Record<QueueAction, string> = {
  send: "Сообщение уже уходит или ушло.",
  cancel: "Сообщение уже уходит или ушло — отменить поздно.",
  edit: "Сообщение уже уходит или ушло — править поздно.",
};

/** Коды chat-service (`scheduling.py`) и причины, которые пишет исполнитель в
 *  строку, не дошедшую до кандидата, — по-русски. */
const QUEUE_CODES: Record<string, string> = {
  scheduled_not_pending: NOT_PENDING.send,
  scheduled_not_found: "Этого сообщения уже нет в очереди.",
  auto_text_not_editable: "У этого сообщения нет текста, который можно править.",
  empty_text: "Пустое сообщение",
  nothing_to_update: "Нечего сохранять: ничего не изменилось.",
  scheduled_at_too_soon: "Время должно быть в будущем",
  // Причины неотправки продолжают фразу «Не ушло: …» — поэтому со строчной.
  provider_unavailable: "канал, из которого сообщение должно уйти, сейчас недоступен.",
  thread_gone: "чат с кандидатом удалён.",
  nothing_to_send: "в сообщении нечего отправлять.",
  send_failed: "канал не принял сообщение.",
};

const QUEUE_FALLBACK: Record<QueueAction, string> = {
  send: "причина неизвестна — проверьте чат.",
  cancel: "Не удалось отменить — попробуйте ещё раз.",
  edit: "Не удалось сохранить — попробуйте ещё раз.",
};

/** Ошибка действия над строкой полоски человеческими словами. Код сервера —
 *  по таблице; уже русское сообщение — как есть; у отправки незнакомая
 *  причина канала («hh send: 503») рекрутёру полезна как есть, а у отмены и
 *  правки сырой код ничего не говорит — там общая фраза. */
export function queueErrorText(action: QueueAction, message?: string | null): string {
  const m = (message ?? "").trim();
  if (m === "scheduled_not_pending") return NOT_PENDING[action];
  if (m && hasOwn(QUEUE_CODES, m)) return QUEUE_CODES[m];
  if (/[А-Яа-яЁё]/.test(m)) return m;
  if (action === "send" && m) return m;
  return QUEUE_FALLBACK[action];
}

/** Ответ 404/409: строка уже не такая, как мы думали (ушла, отменена, взята
 *  исполнителем). Это не «не ушло» — сообщение могло как раз уйти. */
export function isStaleRowStatus(status: number | undefined): boolean {
  return status === 404 || status === 409;
}

/** Строка ошибки под сообщением. «Не ушло:» — только про отправку, которая
 *  точно не состоялась: у неудавшейся отмены или правки такого заголовка нет
 *  (иначе «Не ушло: …» читается как «сообщение не ушло», хотя оно как раз
 *  ушло), как и у ответа «строку уже забрали» и заметки «могло уйти». */
export function queueErrorLine(
  action: QueueAction,
  message?: string | null,
  status?: number,
): string {
  const text = queueErrorText(action, message);
  const notSent = action === "send" && !isStaleRowStatus(status) && !/могло уйти/.test(text);
  return notSent ? `Не ушло: ${text}` : text;
}

/** Отказ chat-service при постановке в очередь, по-русски. */
export function scheduleErrorText(message: string | undefined): string {
  switch (message) {
    case "no_upcoming_meeting":
      return "У кандидата нет назначенной встречи — отправлять «за час» или «за день» не до чего.";
    case "meeting_too_soon":
      return "До встречи осталось меньше выбранного срока — сообщение не успеет уйти вовремя.";
    case "scheduled_at_too_soon":
      return "Время должно быть в будущем";
    case "thread_not_found":
      return "Чат не найден — обновите страницу.";
    default:
      if (message && hasOwn(QUEUE_CODES, message)) return QUEUE_CODES[message];
      return message || "Не удалось запланировать отправку.";
  }
}

// ── Правка строки полоски: что именно отправить на сервер ─────────────────

export interface SchedulePatch {
  text?: string;
  scheduled_at?: string;
  cancel_on_reply?: boolean;
}

/** Что рекрутёр ввёл в окне правки. */
export interface ScheduleDraft {
  text: string;
  /** значение поля «Когда» (datetime-local, точность — минута) */
  when: string;
  flag: boolean;
}

/** Значение поля «Когда» для строки в момент открытия окна правки. */
export function whenInputOf(item: { scheduled_at: string }): string {
  return toLocalInput(new Date(item.scheduled_at));
}

/** Тело PATCH только из того, что рекрутёр ИЗМЕНИЛ, либо текст ошибки.
 *
 *  Время уходит, лишь когда поле «Когда» правили: оно точно до минуты, и
 *  отданное обратно как есть срезало бы секунды у времени отправки (а сервер
 *  счёл бы это выбором рекрутёра и отключил бы тихие часы строки), а за минуту
 *  до отправки правка одного слова упиралась бы в «Время должно быть в
 *  будущем». Проверка «в будущем» — тоже только для изменённого времени. */
export function schedulePatch(
  item: Pick<
    ScheduledChatMessage,
    "text" | "scheduled_at" | "cancel_on_reply" | "attachment" | "auto"
  >,
  draft: ScheduleDraft,
  now: Date = new Date(),
): { body: SchedulePatch } | { error: string } {
  const auto = item.auto ?? null;
  const next = draft.text.trim();
  const textChanged = next !== (item.text ?? "");
  // Автоматическое без предпросмотра правят только по желанию: пустое поле —
  // «не трогать текст», а не «отправить пустое».
  const untouchedAuto = auto !== null && !textChanged;
  if (!next && !item.attachment && !untouchedAuto) return { error: "Пустое сообщение" };
  const body: SchedulePatch = {};
  if (textChanged) body.text = next;
  if (draft.when !== whenInputOf(item)) {
    const at = fromLocalInput(draft.when);
    const err = atError(at, now);
    if (err || !at) return { error: err ?? "Некорректная дата" };
    body.scheduled_at = at.toISOString();
  }
  if (!auto && draft.flag !== item.cancel_on_reply) body.cancel_on_reply = draft.flag;
  return { body };
}

// ── Автоматические сообщения в полоске отложенных («Дева отправит …») ─────

/** Вакансия, по которой уходит автоматическое сообщение. */
export type AutoVacancy = { number: number; title: string };

/** «Продавец-консультант» (№566); null — вакансия строки неизвестна. */
export function vacancyLabel(v: AutoVacancy | null | undefined): string | null {
  if (!v) return null;
  const title = v.title.length > 60 ? `${v.title.slice(0, 59).trimEnd()}…` : v.title;
  return `«${title}» (№${v.number})`;
}

/** Что отправит Дева и по какой вакансии: «отказ по «Продавец» (№566)». */
export function autoWhat(auto: { label: string; vacancy?: AutoVacancy | null }): string {
  const vac = vacancyLabel(auto.vacancy);
  return vac ? `${auto.label} по ${vac}` : auto.label;
}

/** Что показать вместо текста, пока его нет: у «Подумать» сообщения нет вовсе
 *  (только перевод отклика), у стандартных текстов бота — где их править, у
 *  остальных — что текст соберётся по шаблону при отправке. */
export function autoEmptyText(auto: { kind: string; editable: boolean }): string {
  if (auto.kind === "think") return "Сообщения нет — только перевод отклика на hh в «Подумать»";
  if (!auto.editable) return "Стандартный текст (правится в «Настройки → Тексты»)";
  return "Текст соберётся по шаблону при отправке";
}

/** В тексте автоматического сообщения есть `{переменная}`. Личные ссылки (лендинг, бот,
 *  запись) в предпросмотре уже настоящие и переменными не остаются; токеном до отправки
 *  остаются `{zoom_link}` (встречу в Zoom создают при отправке), `{video_link}` и т. п.:
 *  автоматика подставит их тем же способом, что в шаблоне. Тот же вид токена, что у
 *  серверного отрисовщика (shared_db.messages). */
export function hasTemplateVariable(text: string): boolean {
  return /\{(?:[a-z_][a-z0-9_]*|[А-ЯЁ]+)\}/.test(text);
}

/** Подсказка под полем текста, пока в нём есть `{переменная}`. */
export const TEMPLATE_VARIABLE_HINT =
  "Переменные в фигурных скобках (например {zoom_link}) подставятся при отправке — не удаляйте их, если ссылка нужна";

/** Заголовок автоматической строки: «Дева отправит отказ по «Продавец» (№566)». */
export function autoTitle(auto: { label: string; vacancy?: AutoVacancy | null }): string {
  return `Дева отправит ${autoWhat(auto)}`;
}

const ACTION_WORDS: Record<string, string> = {
  "hh_move:discard_by_employer": "переведёт отклик на hh в «Отказ»",
  "hh_move:consider": "переведёт отклик на hh в «Подумать»",
  "candidate_status:not_passed": "закроет кандидатуру (отказ)",
  "manual_review:bot_invite": "оставит кандидата на ручной разбор",
};

/** Что произойдёт в ту же минуту вместе с сообщением, словами для рекрутёра;
 *  null — сообщение единственное, что случится. */
export function autoActionsNote(
  actions: readonly { type: string | null; to: string | null }[],
): string | null {
  const words = actions
    .map((a) => ACTION_WORDS[`${a.type ?? ""}:${a.to ?? ""}`])
    .filter((w): w is string => Boolean(w));
  if (words.length === 0) return null;
  return `В ту же минуту: ${words.join(", ")}.`;
}
